import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  FormFieldError,
  FormFieldLabel,
  getTwoLetterIsoName,
  getLanguageCode,
  DealerAddress,
} from '@triumph/shared';
import GoogleMapReact from 'google-map-react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Label from 'components/_utility/Label';
import request from 'helpers/request';
import { useDispatch, useSelector } from 'react-redux';
import { getDealers, getFormData, getBikeModel } from 'src/redux/app/selectors';
import { setDealers, setFormValues, setSubstep } from 'src/redux/app/actions';
import useDataLayer from 'src/hooks/useDataLayer';
import { STEPPER_HEIGHT } from 'src/config/constants';
import {
  Wrap,
  Title,
  Description,
  PostCodeLookupWrap,
  LocationLookup,
  PostCodeSearchWrap,
  PostCodeInput,
  Or,
  LocationButton,
  SearchButton,
  MapResultsWrap,
  MapOuter,
  Pin,
  Results,
  ResultCard,
  ResultTitle,
  ResultAddress,
  ResultDistance,
  ResultCardNumber,
  ResultCardContent,
} from './YourDealer.styled';

const YourDealerSchema = Yup.object().shape({
  postcode: Yup.string().required('requiredLabel'),
});

let googleMap;
let googleMaps;

const YourDealer = () => {
  const dispatch = useDispatch();
  const pushToDataLayer = useDataLayer();
  const wrapRef = useRef();
  const dealers = useSelector(getDealers);
  const bikeModel = useSelector(getBikeModel);
  const { dealerId: selectedDealer } = useSelector(getFormData);
  const [loadingResults, setLoadingResults] = useState(false);
  const [postcodeError, setPostcodeError] = useState(false);
  const [latLng, setLatLng] = useState({ lat: 0, lng: 0 });
  const markers = dealers.map((dealer) => ({ lat: parseFloat(dealer.Latitude), lng: parseFloat(dealer.Longitude) }));

  const fitBounds = (map, maps) => {
    const bounds = new maps.LatLngBounds();
    markers.forEach((marker) => bounds.extend(marker));
    map.fitBounds(bounds);
    map.setZoom(map.getZoom() - 1);

    if (map.getZoom() > 15) {
      map.setZoom(15);
    }
  };

  const onGoogleApiLoaded = (map, maps) => {
    googleMap = map;
    googleMaps = maps;

    fitBounds(map, maps);
  };

  const handleSearch = async (coords) => {
    try {
      const res = await request('/api/v2/places/search', {
        method: 'POST',
        body: JSON.stringify({
          BikeId: bikeModel.id,
          Input: '',
          PlaceId: '',
          Skip: 0,
          Take: 3,
          Latitude: coords.lat,
          Longitude: coords.lng,
          LanguageCode: getLanguageCode(),
          SiteLanguageCode: getLanguageCode(),
          Distance: 200,
          SearchByDistance: true,
          IsDealerRequest: true,
          IsPurchaseIntentRequest: true,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      setLatLng(coords);
      const dealerCards = res.data.DealerCardData?.DealerCards;

      if (Array.isArray(dealerCards) && dealerCards.length) {
        dispatch(setDealers(dealerCards.slice(0, 3)) || []);
        dispatch(setFormValues({
          dealerId: dealerCards[0].DealerId,
          dealerSearchLocation: document.getElementsByName("postcode")[0]?.value
        }));
      }
    } catch (ex) {
      console.error(ex);
    } // eslint-disable-line

    setLoadingResults(false);
  };

  const handlePostcodeLookup = async ({ postcode }) => {
    setLoadingResults(true);

    try {
      const { data: coords } = await request(`/api/v2/geocode`, {
        method: 'POST',
        body: JSON.stringify({
          postalCode: postcode.trim().replace(/ /g, '+'),
          countryCode: getTwoLetterIsoName()
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      if (!coords) {
        setLoadingResults(false);
        return setPostcodeError(true);
      }

      handleSearch(coords);
    } catch (ex) {
      console.error(ex);
      setLoadingResults(false);
    }
  };

  const handleUseMyLocation = () => {
    setLoadingResults(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          handleSearch({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }),
        () => setLoadingResults(false)
      );
    }
  };

  useEffect(() => {
    if (googleMap && googleMaps) {
      fitBounds(googleMap, googleMaps);
    }
  }, [dealers]);

  useEffect(() => {
    pushToDataLayer('purchaseDealer');

    const y = wrapRef.current.getBoundingClientRect().top - document.body.getBoundingClientRect().top - STEPPER_HEIGHT;
    window.scrollTo(0, y);
  }, []);

  return (
    <Wrap ref={wrapRef}>
      <Title>
        <Label name="dealerTitle"></Label>
      </Title>
      <Description>
        <Label name="dealerDescription"></Label>
      </Description>
      <Formik
        initialValues={{
          postcode: '',
        }}
        validationSchema={YourDealerSchema}
        onSubmit={handlePostcodeLookup}
      >
        {({ values, handleChange }) => (
          <Form>
            <PostCodeLookupWrap>
              <FormFieldLabel>
                <Label name="postcodeLabel" />
                <span className="required">*</span>
              </FormFieldLabel>
              <LocationLookup>
                <PostCodeSearchWrap>
                  <PostCodeInput
                    name="postcode"
                    onChange={e => {
                      setPostcodeError(false);
                      handleChange(e);
                    }}
                  />
                  <SearchButton
                    type="submit"
                    primary
                    large
                    onClick={() => handlePostcodeLookup({ postcode: values.postcode })}
                    disabled={loadingResults || !values.postcode.trim()}
                  >
                    <Label name="searchLabel" />
                  </SearchButton>
                </PostCodeSearchWrap>
                <Or>
                  <Label name="orLabel" />
                </Or>
                <LocationButton type="button" large dark onClick={handleUseMyLocation} disabled={loadingResults}>
                  <svg
                    width="19px"
                    height="19px"
                    viewBox="0 0 28 28"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlnsXlink="http://www.w3.org/1999/xlink"
                  >
                    <defs>
                      <path
                        d="M26.175,13.7499999 C25.6,8.53749997 21.4625,4.39999996 16.2499999,3.82499995 L16.2499999,1.24999994 L13.7499999,1.24999994 L13.7499999,3.82499995 C8.53749997,4.39999996 4.39999996,8.53749997 3.82499995,13.7499999 L1.24999994,13.7499999 L1.24999994,16.2499999 L3.82499995,16.2499999 C4.39999996,21.4625 8.53749997,25.6 13.7499999,26.175 L13.7499999,28.7499999 L16.2499999,28.7499999 L16.2499999,26.175 C21.4625,25.6 25.6,21.4625 26.175,16.2499999 L28.7499999,16.2499999 L28.7499999,13.7499999 L26.175,13.7499999 L26.175,13.7499999 Z M15,23.75 C10.1625,23.75 6.24999996,19.8375 6.24999996,15 C6.24999996,10.1625 10.1625,6.24999996 15,6.24999996 C19.8375,6.24999996 23.75,10.1625 23.75,15 C23.75,19.8375 19.8375,23.75 15,23.75 L15,23.75 Z"
                        id="path-1"
                      ></path>
                    </defs>
                    <g id="Desktop" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g id="Search---Results" transform="translate(-575.000000, -329.000000)">
                        <g id="Group-5" transform="translate(-0.000000, 298.000000)">
                          <g id="Group-4" transform="translate(120.000000, 20.000000)">
                            <g id="Group-3">
                              <g id="location_searching" transform="translate(454.000000, 10.000000)">
                                <polygon
                                  id="Base"
                                  points="-6.24999785e-08 -6.24999075e-08 30.0000001 -6.24999075e-08 30.0000001 30.0000001 -6.24999785e-08 30.0000001"
                                ></polygon>
                                <mask id="mask-2" fill="white">
                                  <use xlinkHref="#path-1"></use>
                                </mask>
                                <g id="Icon" fillRule="nonzero"></g>
                                <rect
                                  id="↳-🎨-Color"
                                  fill="white"
                                  fillRule="evenodd"
                                  mask="url(#mask-2)"
                                  x="0"
                                  y="0"
                                  width="30"
                                  height="30"
                                ></rect>
                              </g>
                            </g>
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                  <Label name="useMyLocationLabel" />
                </LocationButton>
              </LocationLookup>
              {postcodeError ? (
                <FormFieldError>
                  <Label name="invalidPostcodeLabel" />
                </FormFieldError>
              ) : null}
            </PostCodeLookupWrap>
          </Form>
        )}
      </Formik>
      {dealers.length ? (
        <MapResultsWrap>
          <MapOuter>
            <GoogleMapReact
              bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_KEY }}
              center={latLng}
              zoom={10}
              onGoogleApiLoaded={({ map, maps }) => onGoogleApiLoaded(map, maps)}
              yesIWantToUseGoogleMapApiInternals
            >
              {dealers.map((dealer, index) => (
                <Pin
                  key={dealer.DealerUrn}
                  onClick={() => dispatch(setFormValues({ dealerId: dealer.DealerId }))}
                  lat={dealer.Latitude}
                  lng={dealer.Longitude}
                  selected={dealer.DealerId === selectedDealer}
                >
                  {index + 1}
                </Pin>
              ))}
            </GoogleMapReact>
          </MapOuter>
          <Results>
            {dealers.map((dealer, dealerIndex) => (
              <ResultCard
                key={dealer.DealerId}
                selected={selectedDealer === dealer.DealerId}
                onClick={() => dispatch(setFormValues({ dealerId: dealer.DealerId }))}
              >
                <ResultCardNumber selected={selectedDealer === dealer.DealerId}>{dealerIndex + 1}</ResultCardNumber>
                <ResultCardContent>
                  <ResultTitle>{dealer.Title}</ResultTitle>
                  <ResultAddress>
                    <DealerAddress dealer={dealer} format="multi" />
                  </ResultAddress>
                  <ResultDistance>
                    {dealer.Distance} {dealer.DistanceMeasurement}
                  </ResultDistance>
                </ResultCardContent>
              </ResultCard>
            ))}
          </Results>
        </MapResultsWrap>
      ) : null}
      <Button
        fluid
        primary
        type="button"
        onClick={() => [dispatch(setSubstep('b')), pushToDataLayer('dealerSelected')]}
        disabled={postcodeError || !selectedDealer}
      >
        <Label name="dealerContinueButtonLabel"></Label>
      </Button>
    </Wrap>
  );
};

export default YourDealer;
